import { equals } from 'ramda';

import {
  AdherenceTaskType,
  BaseConfigurationType,
  ConfigurationType,
  ETLStreamType,
  ETLTaskType,
  HistoricalDataImportTaskType,
  INTEGRATION_FORM_FIELDS,
  JOB_NAMES,
  MediaImportStreamType,
  RealTimeDataStreamType,
  STREAM_NAMES,
  TASK_NAMES,
  TEMPORARY_FORM_FIELDS,
  USER_SYNC_FILTER_TEAMS_OPTIONS,
  UserSynchronizationTaskType,
} from './types';
import {
  ETL_CONFIGURATION_STATUS,
  ETL_INTEGRATION_TYPE,
  INTEGRATIONS_WITH_ROLE_MAPPING,
  INTEGRATIONS_WITH_TEAM_SELECTOR,
  SUPPORTED_INTEGRATIONS,
  Values,
} from '../../components/configuration/types';
import {
  getSelectedTeams,
  getTeamSelectorMode,
  parseGroupSupervisors,
  parseRoleMapping,
  shouldImportAllTeams,
} from '../../utils/formUtils';

export const createTaskName = (
  jobName: Values<typeof JOB_NAMES> | Values<typeof STREAM_NAMES>,
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>
): string | undefined => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  switch (jobName) {
    case JOB_NAMES.userSynchronization:
      return TASK_NAMES.userSynchronization(type);
    case JOB_NAMES.adherence:
      return TASK_NAMES.adherence(type);
    case JOB_NAMES.historicalData:
      return TASK_NAMES.historicalData(type);
    case STREAM_NAMES.realTimeData:
      return TASK_NAMES.realTimeData(type);
    case STREAM_NAMES.mediaImport:
      return TASK_NAMES.mediaImport(type);
    default: {
      console.warn('createTaskName: Received unknown job name.');
      return undefined;
    }
  }
};

export const isIntegrationTypeSupported = (data: any): Values<typeof ETL_INTEGRATION_TYPE> => {
  if (SUPPORTED_INTEGRATIONS.includes(data)) {
    return data;
  }

  return ETL_INTEGRATION_TYPE.unknown;
};

export const getAllJobs = () => [JOB_NAMES.userSynchronization, JOB_NAMES.adherence, JOB_NAMES.historicalData];

export const getSupportedJobs = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  switch (type) {
    case ETL_INTEGRATION_TYPE.wxcc: {
      return [JOB_NAMES.userSynchronization, JOB_NAMES.adherence, JOB_NAMES.historicalData];
    }
    case ETL_INTEGRATION_TYPE.zoomcc: {
      return [JOB_NAMES.userSynchronization, JOB_NAMES.historicalData];
    }
    case ETL_INTEGRATION_TYPE.msteams: {
      return [JOB_NAMES.userSynchronization];
    }
    case ETL_INTEGRATION_TYPE.wxcalling: {
      return [JOB_NAMES.userSynchronization];
    }
    default: {
      return [];
    }
  }
};

export const getSupportedStreams = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  switch (type) {
    case ETL_INTEGRATION_TYPE.wxcc: {
      return [STREAM_NAMES.realTimeData, STREAM_NAMES.mediaImport];
    }
    case ETL_INTEGRATION_TYPE.zoomcc: {
      return [];
    }
    case ETL_INTEGRATION_TYPE.msteams: {
      return [STREAM_NAMES.mediaImport];
    }
    default: {
      return [];
    }
  }
};

export const getDefaultUserSynchronizationTask = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  const taskProperties: UserSynchronizationTaskType['taskProperties'] = {};

  if ((INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationType.toLowerCase())) {
    taskProperties.roleMapping = {};
  }

  if ((INTEGRATIONS_WITH_TEAM_SELECTOR as readonly string[]).includes(integrationType.toLowerCase())) {
    taskProperties.importAllTeams = true;
    taskProperties.teamSelectorMode = USER_SYNC_FILTER_TEAMS_OPTIONS.includeSelected;
    taskProperties.selectedTeams = [];
  }

  if (integrationType === ETL_INTEGRATION_TYPE.zoomcc) {
    taskProperties.groupSupervisors = [];
  }

  return {
    // Omitting cronExpression as form needs to stay consistent with state when user removes that value
    // cronExpression: '',
    scheduled: false,
    taskName: TASK_NAMES.userSynchronization(type),
    taskProperties,
    taskVariables: {},
  } as UserSynchronizationTaskType;
};

export const getInitialUserSynchronizationTask = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  const taskProperties: UserSynchronizationTaskType['taskProperties'] = {};

  if ((INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationType.toLowerCase())) {
    taskProperties.roleMapping = {};
  }

  return {
    // Omitting cronExpression as form needs to stay consistent with state when user removes that value
    // cronExpression: '',
    scheduled: false,
    taskName: TASK_NAMES.userSynchronization(type),
    taskProperties,
    taskVariables: {},
  } as UserSynchronizationTaskType;
};

export const getEmptyUserSynchronizationTask = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  const result = {
    // Omitting cronExpression as form needs to stay consistent with state when user removes that value
    // cronExpression: '',
    scheduled: false,
    taskName: TASK_NAMES.userSynchronization(type),
    taskProperties: {},
    taskVariables: {},
  } as UserSynchronizationTaskType;

  if ((INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationType.toLowerCase())) {
    result.taskProperties!.roleMapping = {};
  }

  return result;
};

export const getEmptyHistoricalDataTask = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  return {
    // Omitting cronExpression as form needs to stay consistent with state when user removes that value
    // cronExpression: '',
    scheduled: false,
    taskName: TASK_NAMES.historicalData(type),
    taskProperties: {},
    taskVariables: {},
  } as HistoricalDataImportTaskType;
};

export const getEmptyAdherenceTask = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  return {
    // Omitting cronExpression as form needs to stay consistent with state when user removes that value
    // cronExpression: '',
    scheduled: false,
    taskName: TASK_NAMES.adherence(type),
    taskProperties: {},
    taskVariables: {},
  } as AdherenceTaskType;
};

export const getEmptyRealTimeDataStream = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  return {
    enabled: false,
    streamName: TASK_NAMES.realTimeData(type),
    streamProperties: {},
    streamVariables: {},
  } as RealTimeDataStreamType;
};

export const getEmptyMediaImportStream = (integrationType: Values<typeof ETL_INTEGRATION_TYPE>) => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  const streamProperties: { [key: string]: any } = {};

  if (integrationType === ETL_INTEGRATION_TYPE.msteams) {
    streamProperties.azureConnectionStrings = '[]';
  }

  return {
    enabled: false,
    streamName: TASK_NAMES.mediaImport(type),
    streamProperties,
    streamVariables: {},
  } as MediaImportStreamType;
};

export const getEmptyTaskValues = (
  jobName: Values<typeof JOB_NAMES>,
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>
): ETLTaskType => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  switch (jobName) {
    case JOB_NAMES.adherence: {
      return getEmptyAdherenceTask(type);
    }

    case JOB_NAMES.historicalData: {
      return getEmptyHistoricalDataTask(type);
    }

    case JOB_NAMES.userSynchronization: {
      return getEmptyUserSynchronizationTask(type);
    }
    default:
      throw new Error(`getEmptyTaskValues: Received invalid jobName: "${jobName}"!`);
  }
};

export const getInitialTaskValues = (
  jobName: Values<typeof JOB_NAMES>,
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>
): ETLTaskType => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  switch (jobName) {
    case JOB_NAMES.adherence: {
      return getEmptyAdherenceTask(type);
    }

    case JOB_NAMES.historicalData: {
      return getEmptyHistoricalDataTask(type);
    }

    case JOB_NAMES.userSynchronization: {
      return getInitialUserSynchronizationTask(type);
    }
    default:
      throw new Error(`getEmptyTaskValues: Received invalid jobName: "${jobName}"!`);
  }
};

export const getEmptyStreamValues = (
  streamName: Values<typeof STREAM_NAMES>,
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>
): ETLStreamType => {
  const type = integrationType.toLowerCase() as Values<typeof ETL_INTEGRATION_TYPE>;

  switch (streamName) {
    case STREAM_NAMES.realTimeData: {
      return getEmptyRealTimeDataStream(type);
    }
    case STREAM_NAMES.mediaImport: {
      return getEmptyMediaImportStream(type);
    }
    default:
      throw new Error(`getEmptyStreamValues: Received invalid streamName: "${streamName}"!`);
  }
};

export const getEmptyIntegrationValues = (
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>,
  apiUrl?: string // because of API URL is defined in "runtime configuration", it has to be received via parameter
): BaseConfigurationType => {
  const initialValues: BaseConfigurationType = {
    configurationName: '',
    state: ETL_CONFIGURATION_STATUS.inactive,
    integration: integrationType,
    tasks: getAllJobs().map((taskName) => getEmptyTaskValues(taskName, integrationType)), // makes sure tasks are generated in desired order
    streams: getSupportedStreams(integrationType).map((streamName) =>
      getEmptyStreamValues(streamName, integrationType)
    ),
  };

  if (apiUrl) {
    initialValues.configurationProperties = {
      [INTEGRATION_FORM_FIELDS.apiUrl(integrationType).split('.')[1]]: apiUrl,
    };
  }

  return initialValues;
};

export const getInitialIntegrationValues = (
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>,
  apiUrl?: string // because of API URL is defined in "runtime configuration", it has to be received via parameter
): BaseConfigurationType => {
  const initialValues: BaseConfigurationType = {
    configurationName: '',
    state: ETL_CONFIGURATION_STATUS.inactive,
    integration: integrationType,
    tasks: getAllJobs().map((taskName) => getInitialTaskValues(taskName, integrationType)), // makes sure tasks are generated in desired order
    streams: getSupportedStreams(integrationType).map((streamName) =>
      getEmptyStreamValues(streamName, integrationType)
    ),
  };

  if (apiUrl) {
    initialValues.configurationProperties = {
      [INTEGRATION_FORM_FIELDS.apiUrl(integrationType).split('.')[1]]: apiUrl,
    };
  }

  return initialValues;
};

export const formatIntegrationValues = (
  integrationData: ConfigurationType | null,
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>,
  apiUrl?: string
): ConfigurationType => {
  const data = integrationData ?? (getInitialIntegrationValues(integrationType, apiUrl) as ConfigurationType);

  const tasks = getAllJobs().map(
    // makes sure tasks are present in desired order
    (name) => {
      const receivedTask =
        data.tasks.find(({ taskName }) => taskName.endsWith(name)) ?? getEmptyTaskValues(name, integrationType);

      if (receivedTask && receivedTask.taskName.endsWith(JOB_NAMES.userSynchronization)) {
        const modifiedUserSync = {
          ...receivedTask,
          taskProperties: {
            ...receivedTask.taskProperties,
            roleMapping: parseRoleMapping(receivedTask.taskProperties?.roleMapping),
            groupSupervisors:
              integrationType === ETL_INTEGRATION_TYPE.zoomcc
                ? parseGroupSupervisors(receivedTask.taskProperties?.groupSupervisors ?? [])
                : undefined,
            importAllTeams: shouldImportAllTeams(receivedTask.taskProperties),
            teamSelectorMode: getTeamSelectorMode(receivedTask.taskProperties),
            selectedTeams: getSelectedTeams(receivedTask.taskProperties),
          },
        } as UserSynchronizationTaskType;

        if (data.integration.toLowerCase() !== ETL_INTEGRATION_TYPE.zoomcc) {
          delete modifiedUserSync.taskProperties?.groupSupervisors;
        }

        if (!(INTEGRATIONS_WITH_TEAM_SELECTOR as readonly string[]).includes(integrationType.toLowerCase())) {
          delete modifiedUserSync.taskProperties?.includeGroups;
          delete modifiedUserSync.taskProperties?.excludeGroups;
          delete modifiedUserSync.taskProperties?.importAllTeams;
          delete modifiedUserSync.taskProperties?.teamSelectorMode;
          delete modifiedUserSync.taskProperties?.selectedTeams;
        }

        if (!(INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationType.toLowerCase())) {
          delete modifiedUserSync.taskProperties?.roleMapping;
        }

        return modifiedUserSync;
      }

      return receivedTask ?? getEmptyTaskValues(name, data.integration);
    }
  );

  const streams = getSupportedStreams(data.integration).map((name) => {
    const receivedStream =
      data.streams.find(({ streamName }) => streamName.endsWith(name)) ?? getEmptyStreamValues(name, data.integration);
    if (
      receivedStream &&
      data.integration.toLowerCase() === ETL_INTEGRATION_TYPE.msteams &&
      receivedStream.streamName.endsWith(STREAM_NAMES.mediaImport)
    ) {
      let azureConnectionStrings;
      if (receivedStream.streamProperties?.azureConnectionStrings) {
        azureConnectionStrings = JSON.parse(receivedStream.streamProperties?.azureConnectionStrings);
      } else {
        azureConnectionStrings = [];
      }
      if (azureConnectionStrings.length === 0) {
        // otherwise the form will not be pristine
        azureConnectionStrings.push(undefined);
      }
      return {
        ...receivedStream,
        streamProperties: {
          ...receivedStream.streamProperties,
          azureConnectionStrings,
        },
      };
    }
    return receivedStream ?? getEmptyStreamValues(name, data.integration);
  });

  return {
    ...data,
    integration:
      ETL_INTEGRATION_TYPE[data.integration.toLowerCase() as keyof typeof ETL_INTEGRATION_TYPE] ??
      ETL_INTEGRATION_TYPE.unknown,
    tasks,
    streams,
  } as ConfigurationType;
};

export const cleanUpIntegrationData = (integrationData: ConfigurationType): ConfigurationType => {
  const comparedTasks = getAllJobs().map((taskName) => {
    // prevents mismatch on ids
    const emptyTask = { ...getEmptyTaskValues(taskName, integrationData.integration), id: undefined };
    const foundTask = integrationData.tasks.find((task) => task.taskName.endsWith(taskName)) ?? { ...emptyTask };

    const taskProperties: any = { ...foundTask.taskProperties };
    /**
     * If this is user sync task, check role mapping and delete empty role ("some role" : []),
     * so that it is correctly identified as empty task that should not be sent to BE.
     */
    if (foundTask.taskName.endsWith(JOB_NAMES.userSynchronization)) {
      if ((INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationData.integration.toLowerCase())) {
        const roleMappingToSubmit: Record<string, string[]> = taskProperties?.roleMapping ?? {};
        Object.keys(roleMappingToSubmit).forEach((key) => {
          if (!roleMappingToSubmit[key].length) {
            delete roleMappingToSubmit[key];
          }
        });
      }

      if ((INTEGRATIONS_WITH_TEAM_SELECTOR as readonly string[]).includes(integrationData.integration.toLowerCase())) {
        // should be filtering teams
        if (!taskProperties?.importAllTeams) {
          switch (taskProperties?.teamSelectorMode) {
            case USER_SYNC_FILTER_TEAMS_OPTIONS.includeSelected:
              delete taskProperties.excludeGroups;
              taskProperties.includeGroups = JSON.stringify(taskProperties?.selectedTeams);
              break;
            case USER_SYNC_FILTER_TEAMS_OPTIONS.excludeSelected:
              delete taskProperties.includeGroups;
              taskProperties.excludeGroups = JSON.stringify(taskProperties?.selectedTeams);
              break;
            default:
              break;
          }
        } else {
          // otherwise don't send anything in the response
          delete taskProperties.includeGroups;
          delete taskProperties.excludeGroups;
        }
      }

      if (integrationData.integration.toLowerCase() === ETL_INTEGRATION_TYPE.zoomcc) {
        taskProperties.groupSupervisors = JSON.stringify(taskProperties?.groupSupervisors ?? []);
      } else {
        delete taskProperties.groupSupervisors;
      }

      // want to delete every time because BE data is
      delete taskProperties.importAllTeams;
      delete taskProperties.teamSelectorMode;
      delete taskProperties.selectedTeams;
    }

    const newTask = {
      ...foundTask,
      taskProperties,
    };

    // check again and don't send tasks when they are empty
    // prevents mismatch on ids
    if (equals(emptyTask, { ...newTask, id: undefined })) {
      return undefined;
    }

    if (foundTask.taskName.endsWith(JOB_NAMES.userSynchronization)) {
      if ((INTEGRATIONS_WITH_ROLE_MAPPING as readonly string[]).includes(integrationData.integration.toLowerCase())) {
        taskProperties.roleMapping = JSON.stringify(taskProperties?.roleMapping);
      }
    }

    return newTask;
  }) as ETLTaskType[];

  const supportedStreams = getSupportedStreams(integrationData.integration) as Array<string>;

  const comparedStreams = supportedStreams.map((streamName) => {
    const emptyStream = {
      ...getEmptyStreamValues(streamName as Values<typeof STREAM_NAMES>, integrationData.integration),
      id: undefined,
    };
    let foundStream = integrationData.streams.find((stream) => stream.streamName.endsWith(streamName)) ?? {
      ...emptyStream,
    };
    /**
     * Filter out the empty values from azureConnectionStrings array form
     */
    if (
      integrationData.integration.toLowerCase() === ETL_INTEGRATION_TYPE.msteams &&
      foundStream.streamName.endsWith(STREAM_NAMES.mediaImport)
    ) {
      foundStream = {
        ...foundStream,
        streamProperties: {
          azureConnectionStrings: JSON.stringify(
            foundStream.streamProperties?.azureConnectionStrings.filter((s: string) => s != null && s !== '')
          ),
        },
      };
    }

    // filter all empty streams except for webexcc real time data, since it is required
    if (
      !foundStream.streamName.endsWith(STREAM_NAMES.realTimeData) &&
      equals(emptyStream, { ...foundStream, id: undefined })
    ) {
      return undefined;
    }

    return foundStream;
  });

  const dataToSubmit = {
    ...integrationData,
    integration: integrationData.integration.toUpperCase(), // backend accepts only uppercase value
    // only non-empty tasks should stay within the array
    tasks: comparedTasks.filter((s) => s != null),
    streams: comparedStreams.filter((s) => s != null),
    configurationProperties: integrationData.configurationProperties ?? {},
    configurationVariables: integrationData.configurationVariables ?? {},
  } as ConfigurationType;

  // eslint-disable-next-line guard-for-in,no-restricted-syntax -- remove data that should not be present in request
  for (const field in TEMPORARY_FORM_FIELDS) {
    delete dataToSubmit[field as keyof ConfigurationType];
  }

  return dataToSubmit;
};
